
import moment from 'moment';
import { ControlContainer } from '@angular/forms';
import type { FormGroup, UntypedFormControl } from '@angular/forms';
import { first } from 'rxjs/operators';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, Inject, Input } from '@angular/core';
import type { SubmittableFormGroup } from '../..';
import type { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import type { Observable } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import type { UserPrefs } from 'src/app/user/user-preferences/shared/user-prefs';

/**
 * Component for displaying date/time range filters
 */
@Component({
  selector: 'mtx-date-time-picker',
  standalone: false,
  templateUrl: './date-time-picker.component.html',
  styleUrls: ['./date-time-picker.component.scss'],
})
export class DateTimePickerComponent implements OnInit, OnChanges {

  /** Formgroup passed from element */
  @Input() public tgtFormGroup?: SubmittableFormGroup | FormGroup;

  @Input({ required: true }) public tgtFormControlNameDate!: string;

  @Input() public tgtFormControlNameTime?: string;

  /** Pre-seeds the time spinner when no date is selected yet. */
  @Input() public defaultTime?: NgbTimeStruct;

  public userPrefsLoadingRequest$?: Observable<UserPrefs>;

  /** Bindings for ui bootstrap date/time pickers */
  public dateTimeFilters: { date: NgbDateStruct | null; time: NgbTimeStruct | null } =
    {
      date: null,
      time: null,
    };

  public dispValue = '';

  public useMeridian = false;

  private fcd?: UntypedFormControl;

  private fct?: UntypedFormControl;

  private currentSettings: UserPrefs | undefined = undefined;

  public constructor(
    @Inject(ControlContainer) private readonly controlContainer: ControlContainer,
    @Inject(AuthService) private readonly authService: AuthService,
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.tgtFormGroup && !changes.tgtFormGroup.firstChange && this.tgtFormGroup) {
      this.fcd = this.tgtFormGroup.get(this.tgtFormControlNameDate) as UntypedFormControl;
      if (this.tgtFormControlNameTime) {
        this.fct = this.tgtFormGroup.get(this.tgtFormControlNameTime) as UntypedFormControl;
      }
      this.setDateTime();
    }
  }

  /**
   * Sets initial filters
   */
  public ngOnInit(): void {
    // todo: could this occur later?
    this.fcd = this.controlContainer.control?.get(this.tgtFormControlNameDate) as UntypedFormControl;
    if (this.tgtFormControlNameTime) {
      this.fct = this.controlContainer.control?.get(this.tgtFormControlNameTime) as UntypedFormControl;
    }
    this.getSettings();
    this.setDateTime();

    this.tgtFormGroup?.valueChanges.pipe(first()).subscribe(() => {
      this.setDateTime();
    });
  }

  // update formcontrol datetimes

  public updateDateTime(): void {
    const check = this.tgtFormControlNameTime ? moment([
      this.dateTimeFilters.date ? this.dateTimeFilters.date.year : '',
      this.dateTimeFilters.date ? this.dateTimeFilters.date.month - 1 : '',
      this.dateTimeFilters.date ? this.dateTimeFilters.date.day : '',
      this.dateTimeFilters.time ? this.dateTimeFilters.time.hour : '',
      this.dateTimeFilters.time ? this.dateTimeFilters.time.minute : '',
      this.dateTimeFilters.time ? this.dateTimeFilters.time.second : '',
    ]) : moment([
      this.dateTimeFilters.date ? this.dateTimeFilters.date.year : '',
      this.dateTimeFilters.date ? this.dateTimeFilters.date.month - 1 : '',
      this.dateTimeFilters.date ? this.dateTimeFilters.date.day : '',
    ]);

    let dateTime = '';
    let date = '';
    let time = '';

    const checkIsValid = check.isValid();
    if (!this.tgtFormControlNameTime || this.tgtFormControlNameDate === this.tgtFormControlNameTime) {
      // one control
      if (checkIsValid) {
        dateTime = check.format(this.tgtFormControlNameTime ? 'YYYY-MM-DDTHH:mm:ss' : 'YYYY-MM-DD');
        this.dispValue = dateTime;
        this.fcd?.setValue(dateTime);
      } else {
        this.fcd?.setErrors({ invalidDate: 'invalid date' },
          { emitEvent: true });
        this.fcd?.setValue(null);
      }
    } else {
      // two controls
      // eslint-disable-next-line no-lonely-if
      if (checkIsValid) {
        dateTime = check.format('YYYY-MM-DDTHH:mm:ss');
        this.dispValue = dateTime;
        date = check.format('YYYY-MM-DD');
        time = check.format('HH:mm:ss');
        this.fcd?.setValue(date);
        this.fct?.setValue(time);
      } else {
        this.fcd?.setErrors({ invalidDate: 'invalid date' },
          { emitEvent: true });
        this.fct?.setErrors({ invalidTime: 'invalid time' },
          { emitEvent: true });
        this.fcd?.setValue(null);
        this.fct?.setValue(null);
      }
    }
    this.fcd?.markAsTouched({ onlySelf: true });
    this.fct?.markAsTouched({ onlySelf: true });
    this.fcd?.updateValueAndValidity();
    this.fct?.updateValueAndValidity();
  }

  public setToday(now = false): void {
    const mom = moment();
    this.dateTimeFilters.date = {
      day: mom.date(),
      month: mom.month() + 1,
      year: mom.year(),
    };
    this.dateTimeFilters.time = {
      hour: now ? mom.hour() : 0,
      minute: now ? mom.minute() : 0,
      second: 0,
    };

    this.updateDateTime();
  }

  public clearDate(): void {
    this.fcd?.setValue(null);
    this.fct?.setValue(null);
    this.setDateTime();
  }

  // update internal component date/times
  private setDateTime(): void {
    if (this.fcd?.value === null) {
      this.dispValue = '';
      if (this.defaultTime && this.tgtFormControlNameTime) {
        this.dateTimeFilters.time = this.defaultTime;
      }
      return;
    }

    let mom = this.tgtFormControlNameTime ? moment(`${this.fcd?.value}T${this.fct?.value}`, 'YYYY-MM-DDTHH:mm:ss') : moment(`${this.fcd?.value}`, 'YYYY-MM-DD');

    // date + time formcontrols are the same
    if (this.tgtFormControlNameDate === this.tgtFormControlNameTime) {
      mom = moment(this.fcd?.value as string, 'YYYY-MM-DDTHH:mm:ss');
    }

    if (mom.isValid()) {
      this.dateTimeFilters.date = {
        year: mom.year(),
        day: mom.date(),
        month: mom.month() + 1,
      };
      if (this.tgtFormControlNameTime) {
        this.dateTimeFilters.time = {
          hour: mom.hour(),
          minute: mom.minute(),
          second: mom.second(),
        };
      }

      this.dispValue = mom.format(this.tgtFormControlNameTime ? 'YYYY-MM-DDTHH:mm:ss' : 'YYYY-MM-DD');
    } else {
      this.dispValue = '';
      this.dateTimeFilters.date = null;
      this.dateTimeFilters.time = null;
    }
  }

  private checkIf24HourClock(): void {
    if (this.currentSettings?.use_24_hour_clock === true) {
      this.useMeridian = false;
    } else {
      this.useMeridian = true;
    }
  }

  private getSettings(): void {
    this.userPrefsLoadingRequest$ = this.authService.getCurrentUserPrefs();
    this.userPrefsLoadingRequest$.subscribe({
      next: (res: UserPrefs) => {
        this.userPrefsLoadingRequest$ = undefined;
        this.currentSettings = res;
        this.checkIf24HourClock();
      },
      error: () => {
        this.userPrefsLoadingRequest$ = undefined;
      },
    });
  }

}
